import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { memo, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useDeleteCASTQuestionConfig } from '../../../api/delete-csat-question-config.api';
import { GET_LIST_CAST_QUESTION_CONFIG_QUERY_KEY } from '../../../api/get-csat-question-config.api';
import { AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';
import { GET_LIST_CAST_CHANNEL_QUERY_KEY } from '../../../api/get-csat-channel.api';

export type TDeleteQuestionListModalType = 'DeleteQuestionListModal';
export interface IDeleteQuestionListModalProps {
  questionId: string;
}

const DeleteQuestionListModal = memo(({ questionId }: IDeleteQuestionListModalProps) => {
  const customerModuleModalRootActions = useCustomerModuleModalRootActionsStore();
  const showToast = useCustomToast();
  const queryClient = useQueryClient();

  const isOpenDeleteQuestionListModal = useMemo(() => {
    return customerModuleModalRootActions.checkIsOpenModal('DeleteQuestionListModal');
  }, [customerModuleModalRootActions]);

  const deleteMutation = useDeleteCASTQuestionConfig({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error as AxiosErrorCustom) || 'Error deleting question, please try again';
        const displayMessage = Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage;
        showToast({ message: displayMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ type: 'success', message: 'Question deleted successfully!' });
        // Invalidate queries to refresh the list
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_CAST_QUESTION_CONFIG_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_CAST_CHANNEL_QUERY_KEY],
          }),
        ]);
        customerModuleModalRootActions.closeModal('DeleteQuestionListModal');
      },
    },
  });

  const handleClose = useCallback(() => {
    customerModuleModalRootActions.closeModal('DeleteQuestionListModal');
  }, [customerModuleModalRootActions]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleDomBubbleEvent(e);

      if (!questionId) {
        showToast({ message: 'Question ID is required', type: 'error' });
        return;
      }

      deleteMutation.mutate({
        csatQuestionId: questionId,
      });
    },
    [questionId, deleteMutation]
  );

  const isPending = deleteMutation.isPending;

  return (
    <Modal isOpen={isOpenDeleteQuestionListModal} lockBodyScroll onClose={handleClose} className="w-[500px] p-[32px]">
      <ModalHeader>
        <ModalTitle>Delete Question</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form onSubmit={handleSubmit} id="delete-question-list-form" className="grid gap-[16px]">
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this question?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
          disabled={isPending}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-question-list-form" type="submit" disabled={isPending}>
          {isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteQuestionListModal.displayName = 'DeleteQuestionListModal';
export default DeleteQuestionListModal;
